import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  conversations: [],
  currentConversation: null,
  messages: [],
  isLoading: false,
  error: null,
  isTyping: false,
  typingUsers: [],
  searchQuery: '',
  searchResults: [],
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setConversations: (state, action) => {
      state.conversations = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    setCurrentConversation: (state, action) => {
      state.currentConversation = action.payload;
    },
    setMessages: (state, action) => {
      state.messages = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    addMessage: (state, action) => {
      // Check if message already exists to avoid duplicates
      const existingMessage = state.messages.find(msg => msg.id === action.payload.id);
      if (!existingMessage) {
        state.messages.push(action.payload);
      }
    },
    updateMessage: (state, action) => {
      const { messageId, updates } = action.payload;
      const messageIndex = state.messages.findIndex(msg => msg.id === messageId);
      if (messageIndex !== -1) {
        state.messages[messageIndex] = { ...state.messages[messageIndex], ...updates };
      }
    },
    deleteMessage: (state, action) => {
      state.messages = state.messages.filter(msg => msg.id !== action.payload);
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    setTyping: (state, action) => {
      state.isTyping = action.payload;
    },
    setTypingUsers: (state, action) => {
      state.typingUsers = action.payload;
    },
    addTypingUser: (state, action) => {
      if (!state.typingUsers.includes(action.payload)) {
        state.typingUsers.push(action.payload);
      }
    },
    removeTypingUser: (state, action) => {
      state.typingUsers = state.typingUsers.filter(userId => userId !== action.payload);
    },
    markAsRead: (state, action) => {
      const { conversationId, userId } = action.payload;
      const conversation = state.conversations.find(conv => conv.id === conversationId);
      if (conversation && userId) {
        if (conversation.unreadCount) {
          conversation.unreadCount[userId] = 0;
        }
        conversation.lastMessageRead = true;
      }
    },
    updateConversation: (state, action) => {
      const { conversationId, updates } = action.payload;
      const conversationIndex = state.conversations.findIndex(conv => conv.id === conversationId);
      if (conversationIndex !== -1) {
        const currentConversation = state.conversations[conversationIndex];
        const updatedConversation = { ...currentConversation };
        
        Object.keys(updates).forEach(key => {
          if (key.startsWith('unreadCount.')) {
            if (!updatedConversation.unreadCount) {
              updatedConversation.unreadCount = {};
            }
            const userId = key.replace('unreadCount.', '');
            updatedConversation.unreadCount[userId] = updates[key];
          } else {
            updatedConversation[key] = updates[key];
          }
        });
        
        state.conversations[conversationIndex] = updatedConversation;
      }
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setSearchResults: (state, action) => {
      state.searchResults = action.payload;
    },
    clearChat: (state) => {
      state.messages = [];
      state.currentConversation = null;
      state.isTyping = false;
      state.typingUsers = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setConversations,
  setCurrentConversation,
  setMessages,
  addMessage,
  updateMessage,
  deleteMessage,
  setLoading,
  setError,
  setTyping,
  setTypingUsers,
  addTypingUser,
  removeTypingUser,
  markAsRead,
  updateConversation,
  setSearchQuery,
  setSearchResults,
  clearChat,
  clearError,
} = chatSlice.actions;

// Selectors
export const selectConversations = state => state.chat.conversations;
export const selectCurrentConversation = state => state.chat.currentConversation;
export const selectMessages = state => state.chat.messages;
export const selectChatLoading = state => state.chat.isLoading;
export const selectChatError = state => state.chat.error;
export const selectIsTyping = state => state.chat.isTyping;
export const selectTypingUsers = state => state.chat.typingUsers;
export const selectSearchQuery = state => state.chat.searchQuery;
export const selectSearchResults = state => state.chat.searchResults;
export const selectUnreadMessagesCount = state => {
  const currentUser = state.auth?.user;
  if (!currentUser?.uid) return 0;
  
  const totalUnread = state.chat.conversations.reduce((total, conv) => {
    const userUnreadCount = conv.unreadCount?.[currentUser.uid] || 0;
    return total + userUnreadCount;
  }, 0);
  
  return totalUnread;
};

export default chatSlice.reducer;

